import { Book, HelpCircle, Info } from 'lucide-react';
import { eggCookingTips } from '../../data/eggData';

const EggInstructions = () => {
  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-12 bg-white rounded-xl shadow-md">
      <h2 id="instructions" className="font-display text-2xl md:text-3xl text-gray-800 font-bold mb-6">
        <Book className="inline-block w-7 h-7 mr-2 text-primary-500" />
        How to Use the Egg Timer
      </h2>
      
      <div className="space-y-8">
        <section>
          <h3 className="font-display text-xl text-gray-800 font-semibold mb-4">
            Timer Settings Explained
          </h3>
          
          <div className="pl-4 border-l-4 border-primary-200 mb-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              Our egg timer is designed to help you achieve the perfect boiled egg every time. Customize your settings based on your preferences and starting conditions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-primary-700 mb-2">Egg Readiness</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Choose from six levels of doneness, from soft with runny yolks to very hard with completely set yolks. The timer will adjust automatically based on your selection.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-primary-700 mb-2">Egg Temperature</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Refrigerated eggs will need slightly longer cooking time compared to room temperature eggs. Our timer adds 30 seconds for refrigerated eggs.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-primary-700 mb-2">Egg Size</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Size matters! Small eggs cook faster, while large eggs take longer. The timer adds or subtracts 30 seconds based on egg size.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-primary-700 mb-2">Cooking Method</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                Starting with cold water increases cooking time but can result in easier peeling. Our timer adds 3 minutes for cold water starts.
              </p>
            </div>
          </div>
        </section>
        
        <section>
          <h3 className="font-display text-xl text-gray-800 font-semibold mb-4">
            <Info className="inline-block w-6 h-6 mr-2 text-primary-500" />
            Step by Step Instructions
          </h3>
          
          <ol className="space-y-4 pl-0 md:pl-6">
            <li className="flex gap-4">
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium">1</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Choose your preferred egg readiness</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Select from soft, medium-soft, medium, medium-hard, hard, or very hard eggs by clicking on the corresponding icons.
                </p>
              </div>
            </li>
            
            <li className="flex gap-4">
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium">2</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Adjust your settings</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Select whether your eggs are refrigerated or at room temperature, their size, and if you're starting with boiling or cold water.
                </p>
              </div>
            </li>
            
            <li className="flex gap-4">
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium">3</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Start the timer</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Click the "Start Timer" button to begin the countdown. The egg visualization will fill up as time passes.
                </p>
              </div>
            </li>
            
            <li className="flex gap-4">
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium">4</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Prepare your eggs</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  While the timer is running, prepare your pot with water according to your selected method (cold or already boiling).
                </p>
              </div>
            </li>
            
            <li className="flex gap-4">
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium">5</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Cook according to the timer</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Place eggs in water and cook for the duration of the timer. When the timer completes, your eggs are ready!
                </p>
              </div>
            </li>
            
            <li className="flex gap-4">
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium">6</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Cool and enjoy</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Once done, place eggs in cold water to stop the cooking process and make them easier to peel. Enjoy your perfectly boiled eggs!
                </p>
              </div>
            </li>
          </ol>
        </section>
        
        <section>
          <h3 className="font-display text-xl text-gray-800 font-semibold mb-4">
            <HelpCircle className="inline-block w-6 h-6 mr-2 text-primary-500" />
            Pro Tips for Perfect Eggs
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {eggCookingTips.map((tip) => (
              <div key={tip.id} className="bg-primary-50 p-4 rounded-lg border border-primary-100">
                <h4 className="font-medium text-primary-700 mb-2">{tip.title}</h4>
                <p className="text-gray-700 text-sm">{tip.content}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default EggInstructions;